import { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ThumbUp,
  ThumbUpOutlined,
  ChatBubbleOutline,
  MoreVert,
  Edit,
  Delete,
  Flag,
  PushPin,
  VisibilityOff,
  Visibility,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useTogglePostLike, useDeletePost } from '../../hooks/useCommunityMutations';
import type { CommunityPost } from '../../types/community';
import { COLORS } from '@elevair/shared';
import ConfirmDialog from '../common/ConfirmDialog';

interface PostCardProps {
  post: CommunityPost;
  onEdit?: (post: CommunityPost) => void;
  onReport?: (post: CommunityPost) => void;
  onCommentClick?: (post: CommunityPost) => void;
  onPin?: (postId: string, pinned: boolean) => void;
  onHide?: (postId: string) => void;
  onUnhide?: (postId: string) => void;
  showAdminActions?: boolean;
}

export default function PostCard({
  post,
  onEdit,
  onReport,
  onCommentClick,
  onPin,
  onHide,
  onUnhide,
  showAdminActions = false,
}: PostCardProps) {
  const { user } = useAuth();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const toggleLike = useTogglePostLike();
  const deletePost = useDeletePost();

  const isAuthor = user?._id === post.author._id;
  const isSuperAdmin = user?.role === 'super_admin';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleLike = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await toggleLike.mutateAsync(post._id);
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit?.(post);
  };

  const handleDelete = async () => {
    try {
      await deletePost.mutateAsync(post._id);
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  const handleReport = () => {
    handleMenuClose();
    onReport?.(post);
  };

  const handlePin = () => {
    handleMenuClose();
    onPin?.(post._id, !post.isPinned);
  };

  const handleHide = () => {
    handleMenuClose();
    if (post.isHidden) {
      onUnhide?.(post._id);
    } else {
      onHide?.(post._id);
    }
  };

  const handleCommentClick = () => {
    onCommentClick?.(post);
  };

  // Generate avatar color from user ID
  const getAvatarColor = (id: string) => {
    const colors = [COLORS.primary, COLORS.secondary, COLORS.accent, '#9C27B0', '#FF5722'];
    const index = id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <>
      <Card
        sx={{
          mb: 2,
          position: 'relative',
          opacity: post.isHidden ? 0.6 : 1,
          borderLeft: post.isPinned ? `4px solid ${COLORS.accent}` : 'none',
        }}
      >
        {post.isPinned && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: COLORS.accent,
            }}
          >
            <PushPin sx={{ fontSize: 16 }} />
            <Typography variant="caption" fontWeight={500}>
              Pinned
            </Typography>
          </Box>
        )}

        {post.isHidden && showAdminActions && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: post.isPinned ? 80 : 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Chip
              icon={<VisibilityOff sx={{ fontSize: 14 }} />}
              label="Hidden"
              size="small"
              color="warning"
              sx={{ height: 24 }}
            />
          </Box>
        )}

        <CardContent>
          {/* Author Info */}
          <Box display="flex" alignItems="flex-start" gap={1.5} mb={2}>
            <Avatar
              sx={{
                bgcolor: post.author.avatar ? 'transparent' : getAvatarColor(post.author._id),
                width: 40,
                height: 40,
              }}
              src={post.author.avatar}
            >
              {post.author.firstName?.charAt(0)}
              {post.author.lastName?.charAt(0)}
            </Avatar>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {post.author.firstName} {post.author.lastName}
                </Typography>
                {post.author.tier && post.author.tier !== 'free' && (
                  <Chip
                    label={post.author.tier}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      textTransform: 'capitalize',
                      bgcolor:
                        post.author.tier === 'summit'
                          ? '#FEF3C7'
                          : post.author.tier === 'rise'
                          ? '#E5E7EB'
                          : '#FED7AA',
                      color:
                        post.author.tier === 'summit'
                          ? '#92400E'
                          : post.author.tier === 'rise'
                          ? '#374151'
                          : '#9A3412',
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}
                {post.updatedAt !== post.createdAt && ' (edited)'}
              </Typography>
            </Box>

            {/* Menu Button */}
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>

          {/* Post Title */}
          {post.title && (
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              {post.title}
            </Typography>
          )}

          {/* Post Content */}
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              mb: 2,
            }}
          >
            {post.content}
          </Typography>

          <Divider sx={{ mb: 1.5 }} />

          {/* Actions */}
          <Box display="flex" alignItems="center" gap={2}>
            <Tooltip title={post.isLiked ? 'Unlike' : 'Like'}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  color: post.isLiked ? COLORS.primary : 'text.secondary',
                  '&:hover': { color: COLORS.primary },
                }}
                onClick={handleLike}
              >
                {post.isLiked ? (
                  <ThumbUp sx={{ fontSize: 20 }} />
                ) : (
                  <ThumbUpOutlined sx={{ fontSize: 20 }} />
                )}
                <Typography variant="body2">{post.likesCount}</Typography>
              </Box>
            </Tooltip>

            <Tooltip title="Comments">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  color: 'text.secondary',
                  '&:hover': { color: COLORS.secondary },
                }}
                onClick={handleCommentClick}
              >
                <ChatBubbleOutline sx={{ fontSize: 20 }} />
                <Typography variant="body2">{post.commentsCount}</Typography>
              </Box>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {isAuthor && (
          <MenuItem onClick={handleEdit}>
            <Edit fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {isAuthor && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setDeleteConfirmOpen(true);
            }}
            sx={{ color: 'error.main' }}
          >
            <Delete fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
        {!isAuthor && (
          <MenuItem onClick={handleReport}>
            <Flag fontSize="small" sx={{ mr: 1 }} />
            Report
          </MenuItem>
        )}

        {/* Admin Actions */}
        {showAdminActions && isSuperAdmin && (
          <>
            <Divider />
            <MenuItem onClick={handlePin}>
              <PushPin fontSize="small" sx={{ mr: 1 }} />
              {post.isPinned ? 'Unpin' : 'Pin'} Post
            </MenuItem>
            <MenuItem onClick={handleHide}>
              {post.isHidden ? (
                <>
                  <Visibility fontSize="small" sx={{ mr: 1 }} />
                  Unhide Post
                </>
              ) : (
                <>
                  <VisibilityOff fontSize="small" sx={{ mr: 1 }} />
                  Hide Post
                </>
              )}
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
        loading={deletePost.isPending}
      />
    </>
  );
}
